import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFarmById } from '../../api/farm-service/farmService';
import { getFeatureCategories } from '../../api/farm-service/featureCategoryService';
import slugify from 'slugify';
import Blogs from "../../components/farm/Blogs";
import GallerySection from "./sections/Gallery";
import FarmMapSection from "./sections/FarmMap";
import FarmOverview from "./sections/FarmOverview";
import EssentialInfo from "./sections/EssentialInfo";
import VerifiedBadge from "./sections/VerifiedBadge";
import FarmFeatures from "./sections/FarmFeatures";
import FarmSidebarInfo from "./sections/SidebarInfo";
import TipList from "./sections/TipsList";
import SectionSeparator from "../../components/common/SectionSeparator";
import FarmDetailStyle from "../../assets/stylesheets/farm-detail/FarmDetailPage.module.css";
import Logger from "../../utils/logger";

const log = Logger.create('FarmDetailPage');

const SECTION_SEPARATOR_VARIANT = 'thick'; // Default variant for separators
const SECTION_SEPARATOR_SPACING = 'none'; // Default spacing for separators
const SECTION_SEPARATOR_PADDING = 'medium'; // Default padding for separators

export default function FarmDetailPage() {
  const { farmId, farmName } = useParams();
  const navigate = useNavigate();
  const [farm, setFarm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [featureCategories, setFeatureCategories] = useState([]);

  // 1) Farm information
  useEffect(() => {
    if (!farmId) return;
    setLoading(true);
    getFarmById(farmId)
      .then(res => {
        setFarm(res.data);
        setError(null);
        // Redirect if farmName is missing
        if (!farmName && res.data?.name) {
          const slug = slugify(res.data.name, { lower: true, strict: true });
          navigate(`/farm/${farmId}/${slug}`, { replace: true });
        }
      })
      .catch(err => {
        log.error(err);
        setError('data loading failed');
      })
      .finally(() => setLoading(false));
  }, [farmId, farmName, navigate]);

  // 2) Feature categories
  useEffect(() => {
    getFeatureCategories()
      .then(res => {
        if (res && Array.isArray(res.data)) {
          setFeatureCategories(res.data);
        }
      })
      .catch(err => {
        log.error('Failed to load feature categories', err);
      });
  }, []);

  if (loading) return (
    <main className={FarmDetailStyle["detail-main-content"]} style={{ minHeight: '100vh' }}>
      {/* Empty page during initial loading - inherits proper background color */}
    </main>
  );
  if (error)   return <div className="text-center text-red-500 p-8">{error}</div>;
  if (!farm)   return <div className="text-center p-8">Farm not found.</div>;
  
  const dayNames = [
    'Monday','Tuesday','Wednesday',
    'Thursday','Friday','Saturday','Sunday'
  ];

  const hours =
    farm?.businessHours?.map(({ dayOfWeek, openTime, closeTime }) => [
      dayNames[dayOfWeek],
      `${openTime} - ${closeTime}`,
    ]) || [];
  
  const tipsInfo = farm.tips
    ? farm.tips.map(({ tipTitle, tipText }) => ({
        title: tipTitle,
        text: tipText,
      }))
    : [];  


  // Detect mobile for EssentialInfo
  const isMobile = typeof window !== 'undefined' && window.matchMedia('(max-width: 787px)').matches;

  return (
    <main className={FarmDetailStyle["detail-main-content"]}>
      <section className={FarmDetailStyle["detail-overview"]}>
        {/* farm.name -> title, galleryProps -> images */}
        <GallerySection farm={farm} title={farm.name} />

        <div className={FarmDetailStyle["detail-container"]}>
          <div className={FarmDetailStyle["detail-main"]}>
            {/* VerifiedBadge: title, address, region, fake rating */}
            <VerifiedBadge
              title={farm.name}
              address={`${farm.location.city}, ${farm.location.state}`}
              region={farm.location.region}
              rating={4.5}
            />

            <SectionSeparator
              variant={SECTION_SEPARATOR_VARIANT}
              spacing={SECTION_SEPARATOR_SPACING}
            />

            {/* Farm Features section */}
            <FarmFeatures features={farm.features} categories={featureCategories} />

            <SectionSeparator
              variant={SECTION_SEPARATOR_VARIANT}
              spacing={SECTION_SEPARATOR_SPACING}
            />

            <FarmOverview 
              name={farm.name}
              description={farm.description}
            />

            <SectionSeparator
              variant={SECTION_SEPARATOR_VARIANT}
              spacing={SECTION_SEPARATOR_SPACING}
            />

            {/* Essential Info section (contact/location/hours) */}
            <EssentialInfo 
              address={`${farm.location.city}, ${farm.location.state}`}
              web={farm.webUrl}
              social={farm.socialUrl}
              hours={hours}
              isMobile={isMobile}
            />

            <SectionSeparator
              variant={SECTION_SEPARATOR_VARIANT}
              spacing={SECTION_SEPARATOR_SPACING}
            />

            <TipList tips={tipsInfo} />
              

            {/*  FarmEvents */}
          </div>

          <aside className={FarmDetailStyle["detail-sidebar"]}>
            <FarmSidebarInfo hours={hours} />
          </aside>
        </div>
      </section>

      <div className={FarmDetailStyle["separator-container"]}>
        <SectionSeparator
          variant={SECTION_SEPARATOR_VARIANT}
          spacing={SECTION_SEPARATOR_SPACING}
        />
      </div>

      <section className={`${FarmDetailStyle["farm-map-section"]}`}>
        <FarmMapSection location={farm.location} />
      </section>

      <div className={FarmDetailStyle["separator-container"]}>
        <SectionSeparator
          variant={SECTION_SEPARATOR_VARIANT}
          spacing={SECTION_SEPARATOR_SPACING}
        />
      </div>

      <section className={`${FarmDetailStyle["banner-section"]}`}>
        <div className={FarmDetailStyle["middle-banner"]}>
          <img src="/assets-temp/img/banner-middle.jpg" alt="middle-banner" />
        </div>
      </section>

      <div className={FarmDetailStyle["separator-container"]}>
        <SectionSeparator
          variant={SECTION_SEPARATOR_VARIANT}
          spacing={SECTION_SEPARATOR_SPACING}
        />
      </div>

      <section className={`${FarmDetailStyle["section"]} ${FarmDetailStyle["card-section"]} ${FarmDetailStyle["detail-blog-section"]}`}>
        <div className={FarmDetailStyle["container"]}>
          <Blogs />
        </div>
      </section>
    </main>
  );
}
